/**
 * 计算结果表格的数值展示工具。
 * 按数值量级选择"有效数字"或"小数位"，既避免长串小数，又保证极小值（如 1e-28）不会被截断成 0；
 * 并让对比时"有差异"的数值在显示上也能看出差别。
 */

/** 精度档位：常态第 0 档（6），同一字段内出现差异时逐级提升，最后一档兜底（双精度极限） */
export const SIG_LADDER = [6, 9, 12, 15];

/** 1 ≤ |v| < 1e6 区间各档位保留的小数位（该区间整数部分原样保留，不再按有效数字截断） */
const DECIMALS_BY_SIG: Record<number, number> = { 6: 4, 9: 8, 12: 12, 15: 15 };

/** 科学计数法区间（|v| < 1 或 |v| ≥ 1e6）各档位保留的有效数字（比中间量级更短，常态 4 位） */
const SIG_BY_EXP_RANGE: Record<number, number> = { 6: 4, 9: 7, 12: 10, 15: 13 };

/** 是否为可比较的有效数值（"-" / "—" / "" / null / undefined / 非数字 均视为无效） */
export const isNumValue = (value: any): boolean =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  value !== "-" &&
  value !== "—" &&
  !isNaN(Number(value));

/**
 * 格式化数值，非 0 值永不显示为 0，末尾多余的 0 一律去掉：
 * - 1 ≤ |v| < 1e6：整数部分原样保留 + 固定小数位（常态 4 位），如 123.4568、12345.6789、123456.789
 * - |v| < 1：按有效数字（常态 4 位），如 0.1235、0.001235、0.0001235，极小值自动用科学计数法
 * - |v| ≥ 1e6：按有效数字（常态 4 位），自动用科学计数法，如 1.235e+6、1.235e+21
 */
export const formatBySig = (value: any, sig: number = SIG_LADDER[0]): string => {
  if (!isNumValue(value)) return "-";
  const num = Number(value);
  if (num === 0) return "0";
  const abs = Math.abs(num);
  if (abs >= 1 && abs < 1e6) {
    const decimals = DECIMALS_BY_SIG[sig] ?? DECIMALS_BY_SIG[SIG_LADDER[0]];
    return String(Number(num.toFixed(decimals)));
  }
  // 走到这里只剩 |v| < 1 与 |v| ≥ 1e6：按有效数字（常态 4 位），自动用科学计数法，极小/极大都不会被截断成 0
  const digits = SIG_BY_EXP_RANGE[sig] ?? SIG_BY_EXP_RANGE[SIG_LADDER[0]];
  const str = num.toPrecision(digits);
  return str.includes("e") ? str : String(Number(str));
};

/**
 * 同一字段（计算值 + 各对比值）内自适应精度：
 * 取最低档位，使得「differ 判为有差异」的两个值在显示上也能区分（有差异必须显示不同、无差异必须显示相同），
 * 档位用尽仍不能区分时返回最后一档兜底（1 ≤ |v| < 1e6 区间对应小数位 4 → 8 → 12 → 15，|v| < 1 或 |v| ≥ 1e6 区间对应有效数字 4 → 7 → 10 → 13）。
 */
export const resolveSig = (values: any[], differ: (a: any, b: any) => boolean): number => {
  // 无效值（显示为 "-"/"—"）不参与判定：它们本来就不算差异，若一并比较会因"显示不同但不算差异"把精度顶到上限
  const nums = values.filter((v) => isNumValue(v));
  if (nums.length < 2) return SIG_LADDER[0];
  for (const sig of SIG_LADDER) {
    let consistent = true;
    for (let i = 0; i < nums.length && consistent; i++) {
      for (let j = i + 1; j < nums.length; j++) {
        const differByValue = differ(nums[i], nums[j]);
        const differByText = formatBySig(nums[i], sig) !== formatBySig(nums[j], sig);
        if (differByValue !== differByText) {
          consistent = false;
          break;
        }
      }
    }
    if (consistent) return sig;
  }
  return SIG_LADDER[SIG_LADDER.length - 1];
};

/**
 * 误差百分比展示：0 显示 0%；常规差异显示 x.xx%（带符号）；非 0 但小于 0.01% 显示 <0.01%。
 * 避免出现"误差显示 0.00% 但数值被判为有差异/标红"的自相矛盾。
 */
export const formatErrorPercent = (errorPercent: number): string => {
  if (isNaN(errorPercent) || !isFinite(errorPercent)) return "-";
  if (errorPercent === 0) return "0%";
  return Math.abs(errorPercent) >= 0.01 ? `${errorPercent.toFixed(2)}%` : "<0.01%";
};
